/**
 * Referral-link capture (review 2026-09-14).
 *
 * `/?ref=CODE` used to be read ONLY by /landing, but the Gateway at `/` —
 * where every shared referral link actually lands — never looked at the
 * query string, so the code was dropped. And the code was kept in
 * sessionStorage, which does not survive the "confirm your email" tab, so
 * even a captured code was lost before the account existed.
 *
 * Now: every entry route captures it (App.tsx), it is stored in
 * localStorage with a 14-day TTL, and MainApp consumes it once the user is
 * signed in. sessionStorage is still read as a fallback for sessions that
 * were mid-flow when this shipped.
 */
const KEY = "trybeup_referral_code";
const TTL_MS = 14 * 24 * 60 * 60 * 1000;

export function captureReferralCode(code: string | null | undefined): void {
  const clean = (code || "").trim();
  if (!clean) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ code: clean, ts: Date.now() }));
  } catch { /* private mode — fall back to session-only */ }
  try { sessionStorage.setItem(KEY, clean); } catch { /* ignore */ }
}

/** Reads `?ref=` from a query string and stores it if present. */
export function captureReferralFromSearch(search: string): void {
  if (!search || !search.includes("ref=")) return;
  try {
    captureReferralCode(new URLSearchParams(search).get("ref"));
  } catch { /* malformed query — ignore */ }
}

/** The stored code (if any, and not expired) WITHOUT consuming it — for a
 *  link that continues sign-up in another browser (the email confirmation,
 *  the in-app-browser hand-off), which has none of this one's storage
 *  (review 2026-10-21: the friend's 50 XP was lost at that switch). */
export function peekReferralCode(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { code?: string; ts?: number };
        if (parsed?.code && typeof parsed.ts === "number" && Date.now() - parsed.ts < TTL_MS) return parsed.code;
        if (parsed && typeof parsed === "object") return null;
      } catch { return raw; }
    }
  } catch { /* ignore */ }
  try { return sessionStorage.getItem(KEY) || null; } catch { return null; }
}

/** Returns the stored code (if any, and not expired) and clears both stores. */
export function consumeReferralCode(): string | null {
  let code: string | null = null;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      localStorage.removeItem(KEY);
      try {
        const parsed = JSON.parse(raw) as { code?: string; ts?: number };
        if (parsed?.code && typeof parsed.ts === "number" && Date.now() - parsed.ts < TTL_MS) {
          code = parsed.code;
        }
      } catch {
        // Pre-JSON value written by an older bundle — treat as a bare code.
        code = raw;
      }
    }
  } catch { /* ignore */ }
  try {
    const legacy = sessionStorage.getItem(KEY);
    if (legacy) sessionStorage.removeItem(KEY);
    if (!code && legacy) code = legacy;
  } catch { /* ignore */ }
  return code;
}
